import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  contactInputSchema,
  type ContactDetail,
  type ContactDto,
  type ContactInput,
} from '@lawyer/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import type { z } from 'zod'
import { useProjectOptions } from '../../hooks/useLookups'
import { api } from '../../lib/api'
import { formResolver } from '../../lib/form'
import { ErrorAlert, LoadingBlock } from '../common/Feedback'

type FormValues = z.input<typeof contactInputSchema>

const EMPTY: FormValues = { name: '', email: '', phone: '', projects: [] }

type Props = {
  open: boolean
  contact?: ContactDto | null
  /** Seeds the name when creating — what was typed into the picker that opened this. */
  defaultName?: string
  onClose: () => void
  onSaved?: (contact: ContactDto) => void
}

export function ContactDialog({ open, contact, defaultName = '', onClose, onSaved }: Props) {
  const queryClient = useQueryClient()
  const projects = useProjectOptions()
  const isEdit = Boolean(contact)

  // The row that opened the dialog may come from a list without the project links.
  const detail = useQuery({
    queryKey: ['contact', contact?.id],
    queryFn: () => api.get<ContactDetail>(`/contacts/${contact?.id}`),
    enabled: open && Boolean(contact),
  })

  const form = useForm<FormValues, unknown, ContactInput>({
    resolver: formResolver(contactInputSchema),
    defaultValues: EMPTY,
  })
  const links = useFieldArray({ control: form.control, name: 'projects' })

  useEffect(() => {
    if (!open) return
    if (!contact) {
      form.reset({ ...EMPTY, name: defaultName })
      return
    }
    if (!detail.data) return
    form.reset({
      name: detail.data.name,
      email: detail.data.email ?? '',
      phone: detail.data.phone ?? '',
      projects: detail.data.projects.map((link) => ({
        projectId: link.projectId,
        role: link.role ?? '',
      })),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.id, defaultName, detail.data])

  const save = useMutation({
    mutationFn: (values: ContactInput) =>
      isEdit
        ? api.patch<ContactDto>(`/contacts/${contact?.id}`, values)
        : api.post<ContactDto>('/contacts', values),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] })
      void queryClient.invalidateQueries({ queryKey: ['contact'] })
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['project'] })
      void queryClient.invalidateQueries({ queryKey: ['case'] })
      onSaved?.(saved)
      onClose()
    },
  })

  const { errors } = form.formState
  const chosenProjectIds = (form.watch('projects') ?? []).map((link) => link.projectId)
  const loadingDetail = isEdit && !detail.data

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
        <DialogTitle>{isEdit ? 'עריכת איש קשר' : 'איש קשר חדש'}</DialogTitle>
        <DialogContent dividers>
          {save.error ? <ErrorAlert error={save.error} /> : null}
          {detail.error ? <ErrorAlert error={detail.error} /> : null}
          {loadingDetail && !detail.error ? (
            <LoadingBlock />
          ) : (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField
                label="שם"
                autoFocus
                required
                {...form.register('name')}
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="דואר אלקטרוני"
                  type="email"
                  {...form.register('email')}
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message}
                />
                <TextField
                  label="טלפון"
                  {...form.register('phone')}
                  error={Boolean(errors.phone)}
                  helperText={errors.phone?.message}
                />
              </Stack>

              <Typography variant="subtitle2">פרויקטים</Typography>
              {links.fields.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  איש הקשר אינו משויך לפרויקט. תיקים בפרויקט יורשים את אנשי הקשר שלו.
                </Typography>
              ) : null}
              {links.fields.map((field, index) => (
                <Stack key={field.id} direction="row" spacing={1} alignItems="flex-start">
                  <Controller
                    control={form.control}
                    name={`projects.${index}.projectId`}
                    render={({ field: projectField, fieldState }) => {
                      const options = (projects.data ?? []).filter(
                        (p) => p.id === projectField.value || !chosenProjectIds.includes(p.id)
                      )
                      return (
                        <Autocomplete
                          sx={{ flex: 3 }}
                          options={options}
                          loading={projects.isPending}
                          value={options.find((p) => p.id === projectField.value) ?? null}
                          getOptionLabel={(option) => option.displayName}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          onChange={(_event, option) => projectField.onChange(option?.id ?? '')}
                          noOptionsText="אין פרויקטים"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="פרויקט"
                              required
                              error={Boolean(fieldState.error)}
                              helperText={fieldState.error ? 'יש לבחור פרויקט' : undefined}
                            />
                          )}
                        />
                      )
                    }}
                  />
                  <TextField
                    sx={{ flex: 2 }}
                    label="תפקיד בפרויקט"
                    {...form.register(`projects.${index}.role`)}
                  />
                  <IconButton sx={{ mt: 1 }} onClick={() => links.remove(index)} aria-label="הסרה">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={() => links.append({ projectId: '', role: '' })}
                sx={{ alignSelf: 'flex-start' }}
              >
                שיוך לפרויקט
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>ביטול</Button>
          <Button type="submit" variant="contained" disabled={save.isPending || loadingDetail}>
            שמירה
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
